import React, { useState } from "react";
import { IntlProvider } from "react-intl";

import Albania from "../Language/alb.json";
import English from "../Language/en.json";

export type AppLang = "en" | "alb";

export const Context = React.createContext({
  locale: "alb" as AppLang,
  selectLanguage: (_value: string) => {},
});

export const resolveAppLocale = (raw: string | null | undefined): AppLang => {
  if (!raw) return "alb";
  const value = raw.toLowerCase().trim();
  if (value === "en" || value.startsWith("en-") || value.startsWith("en_")) {
    return "en";
  }
  if (
    value === "alb" ||
    value === "sq" ||
    value.startsWith("sq-") ||
    value.startsWith("sq_")
  ) {
    return "alb";
  }
  return "alb";
};

const detectInitialLocale = (): AppLang => {
  const stored = localStorage.getItem("lang");
  if (stored === "en" || stored === "alb") {
    return stored;
  }
  const browser =
    (typeof navigator !== "undefined" &&
      (navigator.languages?.[0] || navigator.language)) ||
    "";
  if (browser.toLowerCase().startsWith("en")) {
    return "en";
  }
  return resolveAppLocale(browser || "alb");
};

const MESSAGES: Record<AppLang, typeof English> = {
  en: English,
  alb: Albania,
};

const initialLocale = detectInitialLocale();
if (localStorage.getItem("lang") !== initialLocale) {
  localStorage.setItem("lang", initialLocale);
}

const LanguageWrapper = (props: { children: React.ReactNode }) => {
  const [locale, setLocale] = useState<AppLang>(initialLocale);
  const [messages, setMessages] = useState(MESSAGES[initialLocale]);

  function selectLanguage(value: string) {
    const newLocale = resolveAppLocale(value);
    localStorage.setItem("lang", newLocale);
    setLocale(newLocale);
    setMessages(MESSAGES[newLocale]);
    window.location.reload();
  }

  const intlLocale = locale === "alb" ? "sq" : "en";

  return (
    <Context.Provider value={{ locale, selectLanguage }}>
      <IntlProvider
        messages={messages}
        locale={intlLocale}
        defaultLocale="en"
        onError={(err) => {
          if (err.code === "MISSING_DATA") return;
          console.error(err);
        }}
      >
        {props.children}
      </IntlProvider>
    </Context.Provider>
  );
};

export default LanguageWrapper;
